// MotionPace's speed dial, drawn in the browser (#533).
//
// The worker burns this dial into the rendered video (#531,
// packages/containers/generatevideo/processing/speed_dial.py). This module is
// its PREVIEW twin: the same geometry, the same rate rule and the same angle
// arithmetic, drawn on a <canvas> over the editor's player so a user can decide
// whether they want the dial before paying for a render.
//
// EVERY CONSTANT BELOW IS A MIRROR. speed_dial.py is the original and this is a
// copy of it; if one changes the other has to, or the preview stops describing
// the download. speedDial.test.js pins the angle arithmetic against fixtures
// generated from the Python module itself, the same way motionPace.test.js pins
// `speedsFromFrameIndices`.
//
// WHAT IS DELIBERATELY NOT MIRRORED: the supersample-and-downscale antialiasing
// and the three-layer decomposition. Both exist in Python because Pillow has no
// antialiased primitives and no numpy was available; canvas has native
// antialiasing and native gradients, so the same picture comes out of a direct
// draw. `drawSpeedDial` is checked against the burn-in by pixel comparison in
// the browser, not by sharing code.

import { speedsFromFrameIndices } from './motionPace';

// --- rate ------------------------------------------------------------------

/** Seconds of SOURCE time per revolution at 1x. Mirrors REVOLUTION_SECONDS. */
export const REVOLUTION_SECONDS = 2.0;

/**
 * Width of the trailing wedge in degrees. Mirrors SECTOR_DEGREES.
 *
 * The wedge is the ALIAS DEFENCE, not decoration: a bare line is
 * 180-degree-symmetric to the eye, so a wedge always wider than one frame's
 * step is what makes the direction of travel unambiguous.
 */
export const SECTOR_DEGREES = 92.0;

/**
 * The fraction of the wedge one frame's step may use, and so the real ceiling
 * on that step. Mirrors SECTOR_STEP_FRACTION / MAX_STEP_DEGREES.
 *
 * NOT 1.0, BECAUSE THE WEDGE IS NOT OPAQUE AT ITS TRAILING EDGE. SECTOR_STOPS
 * ramps it from alpha 0.0 there to 0.30 at the sweep line, so a step equal to
 * the full width only makes consecutive frames' wedge MASKS overlap — the
 * overlap itself carries alpha ~0.001 and is invisible. 0.6 of the wedge
 * (55.2 deg) carries 0.064, the margin this guard was always assumed to
 * enforce. See speed_dial.py for the measured numbers.
 */
export const SECTOR_STEP_FRACTION = 0.6;
export const MAX_STEP_DEGREES = SECTOR_DEGREES * SECTOR_STEP_FRACTION;

// --- geometry --------------------------------------------------------------

// Dial diameter as a fraction of frame height, and the corner inset as a
// fraction of that diameter. Mirrors DIAMETER_RATIO / INSET_RATIO — the
// approved 72px dial with a 24px inset at 720p.
export const DIAMETER_RATIO = 0.10;
export const INSET_RATIO = 1.0 / 3.0;

// Face geometry in the mock's viewBox units (0..100), scaled by the diameter.
//
// EXPORTED SO THE MIRROR CAN CHECK ITSELF. These are hand-copied from
// speed_dial.py and were the least-guarded part of the port — nothing compared
// them to the Python at all, so a stop table or an alpha could drift and every
// suite would stay green. speedDial.cases.json now carries the worker's own
// values (emitted by generatevideo/benchmark/gen_speed_dial_cases.py) and
// speedDial.test.js asserts each of these against it.
//
// The alphas are the one unit conversion: Pillow holds them as 0-255 bytes and
// canvas wants a 0-1 fraction, so the fixture names its copies `*Alpha255` and
// the test divides rather than comparing the two naively.
export const BACKDROP_STOPS = [[0.0, 0.50], [0.72, 0.47], [0.92, 0.36], [1.0, 0.0]];
export const BACKDROP_RADIUS = 48.0;
export const SECTOR_STOPS = [[0.0, 0.0], [0.55, 0.09], [1.0, 0.30]];
export const SECTOR_AXIS_SPAN = 60.0;
export const SWEEP_RADIUS = 41.0;
export const RING_RADIUS = 43.5;
export const RING_WIDTH = 1.4;
export const RING_ALPHA = 76 / 255;
export const SWEEP_WIDTH = 2.4;
export const SWEEP_ALPHA = 235 / 255;
export const CENTRE_DOT_RADIUS = 2.3;
export const CENTRE_DOT_ALPHA = 230 / 255;

const DEG = Math.PI / 180;

// `_positive` from speed_dial.py: a finite, strictly positive number, or null.
// Booleans are rejected for the same reason Python rejects them — `True` is 1
// there and here, and a flag arriving where a frame rate belongs is corruption
// rather than a rate of one.
const positive = (value) => {
  if (typeof value === 'boolean' || typeof value !== 'number') return null;
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
};

/**
 * Seconds of source time per revolution, slowed if the wedge demands it.
 * Mirrors `revolution_seconds`.
 *
 * The dial steps `360 * stride / (sourceFps * seconds)` degrees per output
 * frame, and the wedge can only defend against aliasing while that step stays
 * inside the VISIBLE part of its own width — MAX_STEP_DEGREES (55.2), not the
 * full 92. At the shipped 15x ceiling the default rate no longer fits at any
 * ordinary source rate: 15x at 30fps steps 90 deg/frame, inside the mask but
 * out at its invisible end. The default rate still fits at and below about
 * 9x/30fps, which covers the 7x default.
 *
 * The rate that governs the step is the SOURCE's. `maxSpeed` bounds the
 * source-frame stride per output frame, and each stride is worth `1 / sourceFps`
 * of source time — the quantity REVOLUTION_SECONDS is denominated in.
 */
export const revolutionSeconds = (maxSpeed, sourceFps) => {
  const peak = positive(maxSpeed);
  const fps = positive(sourceFps);
  if (peak === null || fps === null) {
    return { seconds: REVOLUTION_SECONDS, slowed: false };
  }
  const step = (360.0 * peak) / (fps * REVOLUTION_SECONDS);
  if (step <= MAX_STEP_DEGREES) return { seconds: REVOLUTION_SECONDS, slowed: false };
  return { seconds: (360.0 * peak) / (fps * MAX_STEP_DEGREES), slowed: true };
};

// --- angles ----------------------------------------------------------------

/**
 * Cumulative sweep angle in degrees, one per PACED output frame. Mirrors
 * `paced_angles`.
 *
 * Angle 0 is the first frame; every later frame adds the source time its stride
 * consumed. Cumulating rather than mapping speed to an absolute position is
 * what makes the dial continuous — there is no frame at which it can jump.
 *
 * Speed comes from `speedsFromFrameIndices`, THE canonical recovery. It
 * de-quantises the stored INTEGER indices (a 3.5x stretch is stored as 3, 4, 3,
 * 4, and a dial driven off that raw wobbles +/-15% every frame) and clamps to
 * the ceiling the walk enforced.
 *
 * `maxSpeed` is passed through UNCONVERTED, and callers must pass the curve's
 * own. It is not a wall-clock multiplier: the walk advances `position +=
 * current` with `current` bounded by `maxSpeed`, so the bound is already in
 * this function's units — source frames per OUTPUT frame — at every frame rate.
 *
 * `indices[indices.length - 1] + 1` is handed over as the source length to
 * match the Python exactly. It is only consulted for a single-frame curve,
 * which the guard below has already returned.
 */
export const pacedAngles = (frameIndices, sourceFps, revolutionSec, maxSpeed = null) => {
  const indices = Array.isArray(frameIndices) ? frameIndices : [];
  const count = indices.length;
  // A lone frame has no stride to measure and no next angle to advance to, so
  // the sweep just sits at its start.
  if (count < 2) return new Array(count).fill(0);

  const fps = positive(sourceFps) || 30.0;
  const turn = positive(revolutionSec) || REVOLUTION_SECONDS;
  const perFrame = 360.0 / (fps * turn);
  const strides = speedsFromFrameIndices(
    indices, Number(indices[count - 1]) + 1, maxSpeed,
  );

  const angles = new Array(count);
  angles[0] = 0.0;
  for (let k = 0; k < count - 1; k += 1) {
    angles[k + 1] = angles[k] + perFrame * strides[k];
  }
  return angles;
};

/**
 * The output frame showing a given SOURCE frame — the largest `k` with
 * `frameIndices[k] <= sourceFrame`.
 *
 * THIS IS THE INVERSE THE PREVIEW NEEDS AND THE BURN-IN DOES NOT. The worker
 * draws frame `k` of the dial onto output frame `k`; the editor's player is
 * showing the SOURCE video, so it has to go the other way — from where the
 * playhead is in the source to the output frame that will show it, and from
 * there to that frame's angle. Running the dial off the source clock is what
 * keeps it honest against the picture underneath it: at any instant the preview
 * shows exactly the angle the render will show over exactly that footage.
 *
 * `frameIndices` is non-decreasing (the walk's speed floor is 1x), so this is a
 * binary search rather than a scan — it runs once per animation frame.
 */
export const outputFrameAtSourceFrame = (frameIndices, sourceFrame) => {
  const indices = Array.isArray(frameIndices) ? frameIndices : [];
  const count = indices.length;
  if (count === 0) return -1;
  const target = Number(sourceFrame);
  if (!Number.isFinite(target) || target < Number(indices[0])) return 0;

  let low = 0;
  let high = count - 1;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if (Number(indices[mid]) <= target) low = mid;
    else high = mid - 1;
  }
  return low;
};

/**
 * Everything the overlay needs to draw a curve, or null when it cannot.
 *
 * Reads the PERSISTED record and nothing else — like every other client-side
 * consumer of MotionPace, this never derives a curve (see lib/motionPace.js).
 *
 * `sourceFps` is required, not defaulted: the whole mapping from the player's
 * `currentTime` to a source frame runs through it, and guessing 30 on a 25fps
 * project would drift the dial a full revolution every 20 seconds.
 */
export const buildDialPlan = (speedCurve) => {
  if (!speedCurve || typeof speedCurve !== 'object' || Array.isArray(speedCurve)) {
    return null;
  }
  const frameIndices = speedCurve.frameIndices;
  if (!Array.isArray(frameIndices) || frameIndices.length === 0) return null;
  const sourceFps = positive(speedCurve.sourceFps);
  if (sourceFps === null) return null;

  const maxSpeed = speedCurve.maxSpeed;
  const { seconds, slowed } = revolutionSeconds(maxSpeed, sourceFps);
  return {
    frameIndices,
    sourceFps,
    revolutionSeconds: seconds,
    rateSlowed: slowed,
    angles: pacedAngles(frameIndices, sourceFps, seconds, maxSpeed),
  };
};

/** The dial's angle at a moment of SOURCE playback, in degrees. */
export const angleAtSourceTime = (plan, seconds) => {
  if (!plan) return 0;
  const t = Number(seconds);
  const frame = Number.isFinite(t) && t > 0 ? t * plan.sourceFps : 0;
  const k = outputFrameAtSourceFrame(plan.frameIndices, frame);
  return k < 0 ? 0 : plan.angles[k];
};

// --- placement -------------------------------------------------------------

/**
 * Where the dial sits inside a box of `width` x `height` CSS pixels.
 *
 * Sized off the box's HEIGHT by the same ratio the worker uses against the
 * output frame, which is what makes the preview scale with the player element
 * rather than with the source resolution: the dial covers the same fraction of
 * the picture at any player width, exactly as it will in the download.
 *
 * `MIN_DIAMETER_PX` is deliberately NOT mirrored. It is a floor in OUTPUT
 * pixels — it stops a tiny render drawing a few pixels of mush — and applying
 * it to a CSS box would make the dial grow relative to the picture on a narrow
 * player, which is the one thing this preview must not do. At every resolution
 * the product actually renders (>= 400px tall) the floor is inactive and the
 * ratio alone is what the worker uses too.
 *
 * `watermarkPosition` is the position of the mark that will actually render, or
 * null when none will. The dial takes the corner OPPOSITE a bottom-left mark,
 * mirroring `build_spec` — two burn-ins stacked in one corner is the single
 * collision the default layout cannot dodge on its own.
 */
export const dialGeometry = (width, height, watermarkPosition = null) => {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  const diameter = h * DIAMETER_RATIO;
  if (diameter > Math.min(w, h)) return null;
  const inset = diameter * INSET_RATIO;
  const bottomRight = watermarkPosition === 'bottom-left';
  return {
    diameter,
    inset,
    position: bottomRight ? 'bottom-right' : 'bottom-left',
    // Centre, because that is what the canvas transform wants. The worker's
    // `overlay=x:y` places the dial's top-left corner at the same spot.
    centreX: bottomRight ? w - inset - diameter / 2 : inset + diameter / 2,
    centreY: h - inset - diameter / 2,
  };
};

// --- drawing ---------------------------------------------------------------

const addStops = (gradient, stops, channel) => {
  for (const [offset, alpha] of stops) {
    gradient.addColorStop(offset, `rgba(${channel}, ${alpha})`);
  }
};

/**
 * One dial face, centred on `centreX`/`centreY` with the sweep at `angleDeg`
 * (0 = 12 o'clock, clockwise).
 *
 * The layer order is `_build_layers`': backdrop, then the rigid rotating half
 * (wedge then sweep), then the ring and centre dot on top. Only the wedge and
 * the sweep turn — everything else is rotationally symmetric.
 */
export const drawSpeedDial = (ctx, { centreX, centreY, diameter, angleDeg }) => {
  const unit = diameter / 100.0;
  if (!(unit > 0)) return;

  ctx.save();
  ctx.translate(centreX, centreY);

  // Backdrop: a radial black gradient. Canvas holds the final stop's colour
  // beyond `r1`, which is the flat extrapolation `_interpolate` does.
  const backdrop = ctx.createRadialGradient(0, 0, 0, 0, 0, BACKDROP_RADIUS * unit);
  addStops(backdrop, BACKDROP_STOPS, '0, 0, 0');
  ctx.fillStyle = backdrop;
  ctx.beginPath();
  ctx.arc(0, 0, BACKDROP_RADIUS * unit, 0, Math.PI * 2);
  ctx.fill();

  ctx.save();
  // Canvas rotates clockwise for a positive angle with y pointing down, which
  // is the direction the sweep runs. (Pillow rotates the other way, which is
  // why the Python passes `-angle`.)
  ctx.rotate(angleDeg * DEG);

  // The wedge TRAILS the sweep: it spans SECTOR_DEGREES counter-clockwise from
  // 12 o'clock, which in canvas angles (0 at 3 o'clock, clockwise) is
  // -90 - SECTOR_DEGREES up to -90.
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.arc(0, 0, SWEEP_RADIUS * unit, (-90 - SECTOR_DEGREES) * DEG, -90 * DEG);
  ctx.closePath();
  ctx.clip();
  // The gradient axis is the diameter PERPENDICULAR to the wedge's bisector, so
  // the wedge dims toward the centre as well as toward its trailing edge. A
  // flat angular ramp reads noticeably different from the approved mock.
  const axis = (90.0 - SECTOR_DEGREES / 2.0) * DEG;
  const ax = Math.sin(axis) * (SECTOR_AXIS_SPAN / 2) * unit;
  const ay = -Math.cos(axis) * (SECTOR_AXIS_SPAN / 2) * unit;
  const sector = ctx.createLinearGradient(-ax, -ay, ax, ay);
  addStops(sector, SECTOR_STOPS, '255, 255, 255');
  ctx.fillStyle = sector;
  ctx.fillRect(-diameter, -diameter, diameter * 2, diameter * 2);
  ctx.restore();

  // The sweep: a rounded line from the centre to the rim, and deliberately
  // NOTHING at the tip — the approved design drops the head dot the earlier
  // variants carried.
  ctx.strokeStyle = `rgba(255, 255, 255, ${SWEEP_ALPHA})`;
  ctx.lineWidth = SWEEP_WIDTH * unit;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, -SWEEP_RADIUS * unit);
  ctx.stroke();
  ctx.restore();

  ctx.strokeStyle = `rgba(255, 255, 255, ${RING_ALPHA})`;
  ctx.lineWidth = RING_WIDTH * unit;
  ctx.beginPath();
  ctx.arc(0, 0, RING_RADIUS * unit, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = `rgba(255, 255, 255, ${CENTRE_DOT_ALPHA})`;
  ctx.beginPath();
  ctx.arc(0, 0, CENTRE_DOT_RADIUS * unit, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
};
